import type { Perimeter } from '@atlassian/atl-context';

import { type CategorizedStorageControlsCache, CategoryStorageType } from '../../../types';
import type { CacheLoader } from '../load-storage-controls-data';

let analyticsEnabledGlobalState: boolean | undefined;
let productGlobalState: string | undefined;
let perimeterGlobalState: Perimeter | undefined;
let webStorageControlsEnabledGlobalState: boolean | undefined;
let indexedDbControlsEnabledGlobalState: boolean | undefined;
let localConsentModeGlobalState: boolean | undefined;
let packageDebugModeEnabledGlobalState: boolean | undefined;
let allowUncategorizedCookiesInOverridesGlobalState: boolean | undefined;
let disableIndexedDbConsentChecksGlobalState: boolean | undefined;
let userIdGlobalState: string | undefined;
let cloudIdGlobalState: string | undefined;
let hasInitialized: boolean = false;

let cookiesCacheLoader: CacheLoader | undefined;
let localStorageCacheLoader: CacheLoader | undefined;
let sessionStorageCacheLoader: CacheLoader | undefined;
let indexedDBCacheLoader: CacheLoader | undefined;

let loadCache: (
	storageType: CategoryStorageType,
) => Promise<CategorizedStorageControlsCache | undefined> = async (
	storageType: CategoryStorageType,
) => {
	switch (storageType) {
		case CategoryStorageType.Cookies:
			return await cookiesCacheLoader?.loadStorageControlsData();
		case CategoryStorageType.LocalStorage:
			return await localStorageCacheLoader?.loadStorageControlsData();
		case CategoryStorageType.SessionStorage:
			return await sessionStorageCacheLoader?.loadStorageControlsData();
		case CategoryStorageType.IndexedDB:
			return await indexedDBCacheLoader?.loadStorageControlsData();
		default:
			return undefined;
	}
};

export interface OverrideBscGlobalStateParams {
	loadCache?: typeof loadCache;
	product?: typeof productGlobalState;
}

export interface BscGlobalState {
	analyticsEnabled?: boolean;
	localConsentMode?: boolean;
	product?: string;
	perimeter?: Perimeter;

	webStorageEnabled?: boolean;
	indexedDbEnabled?: boolean;
	allowUncategorizedCookiesInOverrides?: boolean;
	disableIndexedDbConsentChecks?: boolean;

	packageDebugMode?: boolean;
	cookiesCacheLoader?: CacheLoader;
	localStorageCacheLoader?: CacheLoader;
	sessionStorageCacheLoader?: CacheLoader;
	indexedDBCacheLoader?: CacheLoader;
	hasInitialized?: boolean;
	userId?: string;
	cloudId?: string;

	loadCache: (
		storageType: CategoryStorageType,
	) => Promise<CategorizedStorageControlsCache | undefined>;
}

export const getBscGlobalState: () => BscGlobalState = () => {
	return {
		analyticsEnabled: analyticsEnabledGlobalState ?? true,
		product: productGlobalState,
		perimeter: perimeterGlobalState,
		webStorageEnabled: webStorageControlsEnabledGlobalState ?? false,
		indexedDbEnabled: indexedDbControlsEnabledGlobalState ?? false,
		localConsentMode: localConsentModeGlobalState ?? false,
		packageDebugMode: packageDebugModeEnabledGlobalState,
		allowUncategorizedCookiesInOverrides: allowUncategorizedCookiesInOverridesGlobalState ?? false,
		disableIndexedDbConsentChecks: disableIndexedDbConsentChecksGlobalState ?? false,
		cookiesCacheLoader,
		localStorageCacheLoader,
		sessionStorageCacheLoader,
		indexedDBCacheLoader,
		loadCache,
		hasInitialized,
		userId: userIdGlobalState,
		cloudId: cloudIdGlobalState,
	};
};

export const testing_overrideGlobalState = ({
	loadCache: overrideLoadCache,
	product: overrideProduct,
}: OverrideBscGlobalStateParams): void => {
	if (overrideLoadCache) {
		loadCache = overrideLoadCache;
	}
	if (overrideProduct) {
		productGlobalState = overrideProduct;
	}
};

export const clearGlobalState = (): void => {
	analyticsEnabledGlobalState = undefined;
	productGlobalState = undefined;
	webStorageControlsEnabledGlobalState = undefined;
	indexedDbControlsEnabledGlobalState = undefined;
	localConsentModeGlobalState = undefined;
	packageDebugModeEnabledGlobalState = undefined;
	allowUncategorizedCookiesInOverridesGlobalState = undefined;
	disableIndexedDbConsentChecksGlobalState = undefined;
	userIdGlobalState = undefined;
	cloudIdGlobalState = undefined;

	cookiesCacheLoader = undefined;
	localStorageCacheLoader = undefined;
	sessionStorageCacheLoader = undefined;
	indexedDBCacheLoader = undefined;
	hasInitialized = false;
};

/**
 * Internal: used only by initialize-global-state to mutate state.
 * Avoids circular dependency by keeping load-storage-controls-data and logger out of core.
 */
export interface InternalStateUpdates {
	productGlobalState?: string;
	webStorageControlsEnabledGlobalState?: boolean;
	indexedDbControlsEnabledGlobalState?: boolean;
	allowUncategorizedCookiesInOverridesGlobalState?: boolean;
	disableIndexedDbConsentChecksGlobalState?: boolean;
	localConsentModeGlobalState?: boolean;
	analyticsEnabledGlobalState?: boolean;
	perimeterGlobalState?: Perimeter;
	cookiesCacheLoader?: CacheLoader;
	localStorageCacheLoader?: CacheLoader;
	sessionStorageCacheLoader?: CacheLoader;
	indexedDBCacheLoader?: CacheLoader;
	packageDebugModeEnabledGlobalState?: boolean;
	hasInitialized?: boolean;
	cloudIdGlobalState?: string;
	userIdGlobalState?: string;
}

export function __internalApplyInitialState(updates: InternalStateUpdates): void {
	if (updates.productGlobalState !== undefined) {
		productGlobalState = updates.productGlobalState;
	}
	if (updates.webStorageControlsEnabledGlobalState !== undefined) {
		webStorageControlsEnabledGlobalState = updates.webStorageControlsEnabledGlobalState;
	}
	if (updates.indexedDbControlsEnabledGlobalState !== undefined) {
		indexedDbControlsEnabledGlobalState = updates.indexedDbControlsEnabledGlobalState;
	}
	if (updates.allowUncategorizedCookiesInOverridesGlobalState !== undefined) {
		allowUncategorizedCookiesInOverridesGlobalState =
			updates.allowUncategorizedCookiesInOverridesGlobalState;
	}
	if (updates.disableIndexedDbConsentChecksGlobalState !== undefined) {
		disableIndexedDbConsentChecksGlobalState = updates.disableIndexedDbConsentChecksGlobalState;
	}
	if (updates.localConsentModeGlobalState !== undefined) {
		localConsentModeGlobalState = updates.localConsentModeGlobalState;
	}
	if (updates.analyticsEnabledGlobalState !== undefined) {
		analyticsEnabledGlobalState = updates.analyticsEnabledGlobalState;
	}
	if (updates.perimeterGlobalState !== undefined) {
		perimeterGlobalState = updates.perimeterGlobalState;
	}
	if (updates.cookiesCacheLoader !== undefined) {
		cookiesCacheLoader = updates.cookiesCacheLoader;
	}
	if (updates.localStorageCacheLoader !== undefined) {
		localStorageCacheLoader = updates.localStorageCacheLoader;
	}
	if (updates.sessionStorageCacheLoader !== undefined) {
		sessionStorageCacheLoader = updates.sessionStorageCacheLoader;
	}
	if (updates.indexedDBCacheLoader !== undefined) {
		indexedDBCacheLoader = updates.indexedDBCacheLoader;
	}
	if (updates.packageDebugModeEnabledGlobalState !== undefined) {
		packageDebugModeEnabledGlobalState = updates.packageDebugModeEnabledGlobalState;
	}
	if (updates.hasInitialized !== undefined) {
		hasInitialized = updates.hasInitialized;
	}
	if (updates.cloudIdGlobalState !== undefined) {
		cloudIdGlobalState = updates.cloudIdGlobalState;
	}
	if (updates.userIdGlobalState !== undefined) {
		userIdGlobalState = updates.userIdGlobalState;
	}
}

export function __internalGetHasInitialized(): boolean {
	return hasInitialized;
}

export function __internalGetProductGlobalState(): string | undefined {
	return productGlobalState;
}

export function __internalGetWebStorageControlsEnabledGlobalState(): boolean | undefined {
	return webStorageControlsEnabledGlobalState;
}

export function __internalGetIndexedDbControlsEnabledGlobalState(): boolean | undefined {
	return indexedDbControlsEnabledGlobalState;
}

export function __internalGetAllowUncategorizedCookiesInOverridesGlobalState():
	| boolean
	| undefined {
	return allowUncategorizedCookiesInOverridesGlobalState;
}

export function __internalGetDisableIndexedDbConsentChecksGlobalState(): boolean | undefined {
	return disableIndexedDbConsentChecksGlobalState;
}
